import { v4 as uuidv4 } from 'uuid';
import { SPONSERED_RESULTS_LIMIT } from '~/modules/config';
import { buildListingCategory } from '~/modules/helpers/categoryHelper';
import { Routes } from '~/modules/routes';
import { SearchContext, SearchContextListing, SearchResult, SearchResultListing } from '~/schemas/search-schema-v1';

export interface BuildSearchResultOptions {
    isRecommendedResult?: boolean;
    rankingInformation?: boolean;
}

const buildListingUrl = (listingId: string, options?: BuildSearchResultOptions) => {
    let ret = `/${Routes.LISTING}?listingId=${listingId}`;
    if (options?.isRecommendedResult) {
        ret += '&channel=recommended';
    }
    return ret;
};

type RawListing = SearchContextListing &
    Omit<SearchResultListing, 'context' | 'listingCategories' | 'chatEnabled'> & {
        collectionName: string;
        id: string;
        rankingInfo: string;
        listingCategories: string[];
        chatEnabled?: string | boolean;
    };

const buildCategories = (listing: RawListing) => {
    return listing.listingCategories?.map(buildListingCategory);
};

const parseListing = (
    rawListing: RawListing,
    options?: BuildSearchResultOptions,
): Omit<SearchResultListing, 'context'> => {
    return {
        ...rawListing,
        listingUrl: buildListingUrl(rawListing.oafId, options),
        oafId: rawListing.oafId,
        listingCategories: buildCategories(rawListing),
        chatEnabled:
            typeof rawListing.chatEnabled === 'string'
                ? rawListing.chatEnabled.toLocaleLowerCase() === 'true'
                : Boolean(rawListing.chatEnabled),
    };
};

type SearchResponse = {
    value: string;
    originLevel1: string;
    originLevel2?: string;
    language: string;
    advancedQuery: string;
    queryText?: string;
    searchQueryUid: string;
    queryPipeline: string;
    duration: number;
    totalCount: number;
    listings: RawListing[];
    featured?: RawListing[];
};

const getSearchResult = (data: SearchResponse, options?: BuildSearchResultOptions): SearchResult => {
    // to keep support to older Search API versions we try to grab sponsored listings that have sponsored=true (v1, v2)
    const { listings, featured } = data.listings.reduce<{
        listings: SearchResultListing[];
        featured: Omit<SearchResultListing, 'context'>[];
    }>(
        (acc, rawListing) => {
            const {
                documentUri,
                documentUrl,
                documentUriHash,
                documentPosition,
                rankingModifier,
                sourceName,
                collectionName,
                id,
                rankingInfo,
                ...listing
            } = rawListing;
            if (rankingInfo) {
                console.log(`Ranking information:\n${rawListing.title} (${id})\n\n${rankingInfo}`);
            }

            if (!listing.sponsored) {
                acc.listings.push({
                    ...parseListing(rawListing, options),
                    context: {
                        documentUri: documentUri,
                        documentUrl: documentUrl,
                        documentUriHash: documentUriHash,
                        documentPosition: documentPosition,
                        rankingModifier: rankingModifier,
                        sourceName: sourceName,
                        collection: collectionName,
                    } as SearchContextListing,
                } as SearchResultListing);
            } else {
                acc.featured.push(parseListing(rawListing, options));
            }
            return acc;
        },
        { listings: [], featured: [] },
    );
    if (data.featured && data.featured.length > 0) {
        featured.unshift(
            ...data.featured.map(
                (listing) =>
                    ({
                        ...parseListing(listing, options),
                        sponsored: true,
                    }) as Omit<SearchResultListing, 'context'>,
            ),
        );
    }

    return {
        id: data.queryText ? data.searchQueryUid : uuidv4(),
        value: data.value,
        listings,
        featured: featured.slice(0, SPONSERED_RESULTS_LIMIT),
        totalSearchResults: data.totalCount,
        context: {
            originLevel1: data.originLevel1,
            language: data.language,
            searchQueryUid: data.searchQueryUid,
            queryPipeline: data.queryPipeline,
            duration: data.duration,
            numberOfResults: listings.length,
            queryText: data.queryText,
            advancedQuery: data.advancedQuery,
            contextual: true,
        } as SearchContext,
        isLoading: false,
        isError: false,
        isEmpty: data.totalCount === 0,
    };
};

// @ts-expect-error * Automated Type Error Fix
const buildSearchResults = (response, options?: BuildSearchResultOptions): Array<SearchResult> => {
    if (!response.data || response.data.length === 0) {
        return [];
    }
    const { data } = response;

    if (Array.isArray(data)) {
        return data.map((result) => {
            return getSearchResult(result, options);
        });
    }
    return [getSearchResult(data, options)];
};

export { buildListingUrl, buildSearchResults };
